import { apiClient, ApiResponse } from './api'
import { API_ENDPOINTS } from '@/constants/api'

// Customer-related types
export interface Customer {
  id: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  dateOfBirth?: string
  status: 'active' | 'inactive' | 'blocked'
  addresses: CustomerAddress[]
  totalOrders: number
  totalSpent: number
  averageOrderValue: number
  lastOrderDate?: string
  createdAt: string
  updatedAt: string
}

export interface CustomerAddress {
  id: string
  type: 'billing' | 'shipping'
  isDefault: boolean
  firstName: string
  lastName: string
  company?: string
  address1: string
  address2?: string
  city: string
  state: string
  postalCode: string
  country: string
  phone?: string
}

export interface CustomerFilters {
  page?: number
  limit?: number
  search?: string
  status?: 'active' | 'inactive' | 'blocked'
  dateFrom?: string
  dateTo?: string
  sortBy?: 'createdAt' | 'lastOrderDate' | 'totalSpent' | 'email'
  sortOrder?: 'asc' | 'desc'
}

export interface CustomerListResponse {
  customers: Customer[]
  total: number
  page: number
  limit: number
  totalPages: number
}

// Customer management service functions
export const customerService = {
  // Get customer list with filters and pagination
  async getCustomers(filters?: CustomerFilters): Promise<CustomerListResponse> {
    const response = await apiClient.get<ApiResponse<CustomerListResponse>>(API_ENDPOINTS.CUSTOMERS.LIST, {
      params: filters,
    })
    return response.data.data
  },

  // Get customer by ID
  async getCustomer(customerId: string): Promise<Customer> {
    const response = await apiClient.get<ApiResponse<Customer>>(API_ENDPOINTS.CUSTOMERS.DETAIL(customerId))
    return response.data.data
  },

  // Create new customer
  async createCustomer(
    customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'totalOrders' | 'totalSpent' | 'averageOrderValue'>
  ): Promise<Customer> {
    const response = await apiClient.post<ApiResponse<Customer>>(API_ENDPOINTS.CUSTOMERS.CREATE, customerData)
    return response.data.data
  },

  // Update customer (PATCH)
  async updateCustomer(customerId: string, customerData: Partial<Customer>): Promise<Customer> {
    const response = await apiClient.patch<ApiResponse<Customer>>(
      API_ENDPOINTS.CUSTOMERS.UPDATE(customerId),
      customerData
    )
    return response.data.data
  },

  // Delete customer
  async deleteCustomer(customerId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.CUSTOMERS.DELETE(customerId))
  },

  // Search customers
  async searchCustomers(query: string): Promise<Customer[]> {
    const response = await apiClient.get<ApiResponse<Customer[]>>(API_ENDPOINTS.CUSTOMERS.SEARCH, {
      params: { q: query },
    })
    return response.data.data
  },

  // Export customers
  async exportCustomers(filters?: CustomerFilters): Promise<Blob> {
    const response = await apiClient.get(API_ENDPOINTS.CUSTOMERS.EXPORT, {
      params: filters,
      responseType: 'blob',
    })
    return response.data
  },

  // Import customers
  async importCustomers(file: File): Promise<{
    imported: number
    updated: number
    errors: Array<{ row: number; message: string }>
  }> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await apiClient.post<
      ApiResponse<{
        imported: number
        updated: number
        errors: Array<{ row: number; message: string }>
      }>
    >(API_ENDPOINTS.CUSTOMERS.IMPORT, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data.data
  },
}
